/*
 * Shared Vitesse browser-chrome branding — the tab TITLE + FAVICON every Vitesse
 * web app reuses, so every tab reads "Vitesse - <App>" with the V mark, identically
 * across App / Fleet / AI / future products. ONE source of truth: no per-app favicon
 * asset, no per-app <title> drift. Call applyVitesseBranding('<App>') once at bootstrap.
 *
 * The favicon is the compact V/A mark — the SAME geometry as <vitesse-logo compact>
 * (the wordmark-less mark), with the brand-blue gradient — inlined as an SVG data-URL
 * so it needs no file and stays crisp at any tab size ("clean SVGs always").
 */
// Compact V mark (viewBox 0 0 512 258.9) — mirrors <vitesse-logo compact>. The
// gradient id is favicon-local so it can't collide with the on-page <vitesse-logo>.
const V_MARK_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 258.9">' +
    '<defs><linearGradient id="vfav" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="512" y2="258.9">' +
    '<stop offset="0" stop-color="#1652c9"/><stop offset="0.55" stop-color="#2491db"/><stop offset="1" stop-color="#6fb2f4"/>' +
    '</linearGradient></defs><g fill="url(#vfav)">' +
    '<polygon points="126.08 0 190.85 0 283.98 258.9 218.66 258.9 126.08 0"/>' +
    '<polygon points="385.92 0 321.15 0 264.5 143.24 295.44 229.99 385.92 0"/>' +
    '<polygon points="140.94 107.58 125.53 64.5 0 64.5 0 107.58 140.94 107.58"/>' +
    '<polygon points="148.56 128.9 0 128.9 0 171.99 163.97 171.99 148.56 128.9"/>' +
    '<polygon points="383.39 64.5 366.43 107.58 512 107.58 512 64.5 383.39 64.5"/>' +
    '<polygon points="358.05 128.9 341.1 171.99 512 171.99 512 128.9 358.05 128.9"/>' +
    '</g></svg>';
/** The V-mark favicon as an SVG data-URL — exported in case an app wants it directly. */
export const VITESSE_FAVICON_HREF = `data:image/svg+xml,${encodeURIComponent(V_MARK_SVG)}`;
/**
 * Set the tab title + favicon for a Vitesse web app. The title becomes
 * `Vitesse - <app>` (e.g. applyVitesseBranding('Fleet') → "Vitesse - Fleet"); an
 * empty/omitted app gives bare "Vitesse". The favicon is always the V mark.
 * Idempotent — safe to call again (e.g. to retitle on route change).
 */
export function applyVitesseBranding(app) {
    if (typeof document === 'undefined')
        return;
    document.title = app ? `Vitesse - ${app}` : 'Vitesse';
    let link = document.head.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    link.type = 'image/svg+xml';
    link.href = VITESSE_FAVICON_HREF;
}
/**
 * The watercolour dawn fade behind the splash and the sign-in card — night at
 * the top through to first light at the bottom.
 *
 * A brand constant rather than a token because each app's `index.html` paints
 * its splash before any stylesheet has loaded, so a custom property would not
 * yet resolve there. That copy in static HTML is unavoidable; this one is the
 * source of truth for every app that needs the gradient from script, so a
 * change here reaches all of them and only the HTML copies need syncing.
 */
export const SPLASH_GRADIENT = 'linear-gradient(180deg,#000004 0%,#050a20 13%,#0a1c55 30%,#133595 48%,#1f5ed1 66%,#6ba6f2 84%,#e9f2fd 100%)';
/** id → the subdomain label it lives at. The three are 1:1 with their id. */
const SURFACE_SUB = { app: 'app', ai: 'ai', fleet: 'fleet' };
const SURFACE_PROD = {
    app: 'https://app.vitesseautomation.ai',
    ai: 'https://ai.vitesseautomation.ai',
    fleet: 'https://fleet.vitesseautomation.ai',
};
export const SURFACE_ORDER = ['app', 'ai', 'fleet'];
/**
 * The three surface links for the current environment. `override` names the current
 * surface explicitly (an app that knows its own identity via applyVitesseBranding);
 * when omitted, the current surface is inferred from the first hostname label.
 */
export function surfaceLinks(override) {
    const host = typeof location !== 'undefined' ? location.hostname : '';
    const first = host.split('.')[0] ?? '';
    const knownEntry = Object.entries(SURFACE_SUB).find(([, sub]) => sub === first);
    const known = knownEntry?.[0];
    const current = override ?? known;
    // The domain suffix shared by all siblings (e.g. `local.vitesseautomation.ai`), present only when
    // the page is actually on one of the three — otherwise we fall back to the prod origins.
    const rest = known ? host.slice(first.length + 1) : '';
    const port = typeof location !== 'undefined' && location.port ? `:${location.port}` : '';
    const proto = typeof location !== 'undefined' ? location.protocol : 'https:';
    return SURFACE_ORDER.map((id) => ({
        id,
        href: rest ? `${proto}//${SURFACE_SUB[id]}.${rest}${port}` : SURFACE_PROD[id],
        current: id === current,
    }));
}
/**
 * The absolute origin of ONE sibling surface, for this environment.
 *
 * Same derivation as surfaceLinks (swap the first hostname label, keep the rest,
 * protocol and port; prod origins as the fallback off-surface), exposed on its own
 * because a cross-surface API call needs the origin, not a link. The billing panel
 * is the case that forced it: its endpoints live on the AI worker, so a RELATIVE
 * fetch from app./fleet. hits those Pages sites' SPA fallback and gets index.html
 * back with a 200 — which fails as JSON and reads to the customer as
 * "Could not load your usage" rather than as the wrong host.
 */
export function surfaceOrigin(id) {
    return surfaceLinks().find((l) => l.id === id)?.href ?? SURFACE_PROD[id];
}
/*
 * The identity provider's own widgets, wearing our chrome.
 *
 * Clerk renders the user button and the organization switcher itself, so their
 * hover states are Clerk's defaults rather than ours: the org trigger fills with
 * a grey block, while the theme toggle sitting immediately beside it in the same
 * header grows and turns blue. Two controls, one row, two different languages.
 *
 * This is the icon-button spec from styles/_icon-button.scss expressed in the
 * appearance object, because that is the only surface Clerk exposes — flat, no
 * background, grow to 1.07 and recolor to `--blue` on hover. It lives here, once,
 * for the same reason the mixin does: three apps mounting these widgets is three
 * chances for the treatment to drift.
 *
 * Takes the dark base theme rather than importing it, so base-ui keeps carrying
 * no identity-provider dependency. Generic rather than `unknown` so the caller's
 * own theme type survives the round trip — `unknown` compiles here and then
 * fails at every call site, which is a shared helper nobody can actually use.
 */
export function vitesseClerkAppearance(darkBaseTheme, isDark = false) {
    /*
     * NO TRANSFORM ON A TRIGGER, and the reason is not aesthetic.
     *
     * The icon-button spec grows a control to 1.07 on hover. That is safe for an
     * ICON: a fixed square, laid out on its own, with nothing positioned relative
     * to it. Both of these triggers are neither. Scaling them produced two
     * separate defects:
     *
     *   - the org trigger is an avatar AND a name, so growing the control grew the
     *     logo along with the label, which is not what the gesture is pointing at;
     *   - each trigger ANCHORS a popover. Hovering scaled the trigger and moved the
     *     anchor, so the open menu stepped about a pixel; moving the pointer off
     *     the trigger and onto the menu un-scaled it and the menu stepped back.
     *     Visible only at the viewport widths where the rounding fell the wrong
     *     way, which is what made it look intermittent.
     *
     * So this takes the half of the spec that carries the meaning — flat, no grey
     * block, recolor to `--blue` — and drops the half that assumes an icon. The
     * recolor is aimed at the NAME rather than the whole control, so the logo is
     * left alone.
     */
    /*
     * THE RESTING COLOUR IS SET, not inherited.
     *
     * These triggers sit in OUR header, not inside a provider dialog, so the
     * provider's base theme does not govern them — it themes its own modals and
     * popovers. Left alone, the org name rendered in the provider's default dark
     * text on a dark header and simply disappeared.
     *
     * Driven from the token rather than a light/dark branch, so it tracks whatever
     * the header actually is and cannot fall out of step with a theme it was not
     * told about.
     */
    const flatTrigger = {
        background: 'transparent',
        boxShadow: 'none',
        color: 'var(--text-primary)',
        transition: 'color 0.15s ease',
        '&:hover': { background: 'transparent' },
        '&:focus': { background: 'transparent', boxShadow: 'none' },
        // Clerk marks a trigger open while its popover is up; without this the grey
        // block returns for as long as the menu is on screen.
        '&[data-state="open"]': { background: 'transparent' },
    };
    const nameHover = {
        ...flatTrigger,
        // Both identifiers, and the avatar's fallback initials: each is a separate
        // element the provider colours itself.
        '& .cl-organizationPreviewMainIdentifier': { color: 'var(--text-primary)' },
        '& .cl-organizationPreviewSecondaryIdentifier': { color: 'var(--text-secondary)' },
        '& .cl-avatarBox': { color: 'var(--text-primary)' },
        '&:hover .cl-organizationPreviewMainIdentifier': { color: 'var(--blue)' },
        '&[data-state="open"] .cl-organizationPreviewMainIdentifier': { color: 'var(--blue)' },
    };
    return {
        baseTheme: isDark ? darkBaseTheme : undefined,
        elements: {
            organizationSwitcherTrigger: nameHover,
            userButtonTrigger: flatTrigger,
            /*
             * THE PROVIDER'S MEMBER LIST, WEARING OUR ROW.
             *
             * The Members page is Clerk's — it owns invitations, requests, the joined
             * date and the remove action, and reimplementing that to control its looks
             * would mean owning an IdP surface for the sake of a border. So the table
             * keeps its behaviour and takes our row treatment instead: the same bordered
             * bubble, the same wall-to-wall hairlines, the same 12px rhythm as
             * `.base-inventory-row`, so the two member lists in this dialog read as one
             * product rather than two.
             *
             * Values come from the SAME tokens the rest of the dialog uses rather than
             * literals, so this tracks a theme change with everything else instead of
             * being a third palette nobody remembers to update.
             */
            table: {
                border: '1px solid var(--border-color)',
                borderRadius: '10px',
                borderCollapse: 'separate',
                borderSpacing: '0',
                overflow: 'hidden',
            },
            tableHead: {
                background: 'var(--bg-secondary)',
                '& th': {
                    padding: '10px 12px',
                    fontSize: '11px',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'var(--text-tertiary)',
                    borderBottom: '1px solid var(--border-color)',
                },
            },
            // The hairline goes on the CELL, not the row: a border on a <tr> is not
            // painted under `border-collapse: separate`, which is what the rounded
            // corners above require.
            tableBody: {
                '& td': {
                    padding: '12px',
                    borderTop: '1px solid var(--border-color)',
                    fontSize: '13.5px',
                    color: 'var(--text-primary)',
                },
                '& tr:first-child td': { borderTop: '0' },
                '& tr:hover td': { background: 'var(--hover-bg)' },
            },
            organizationPreviewMainIdentifier__memberList: { color: 'var(--text-primary)', fontSize: '13.5px' },
            organizationPreviewSecondaryIdentifier__memberList: { color: 'var(--text-tertiary)', fontSize: '11.5px' },
            /*
             * THE WHOLE DIALOG, WEARING OUR SURFACE.
             *
             * The provider's dark base theme paints its card a mid-grey; the app's own dialogs are pure
             * --bg-primary (black in dark, white in light) with a token border. Driven off the tokens so
             * the two never disagree — a theme flip moves this with everything else rather than leaving a
             * grey box floating in a black app. Applied through the appearance element map (not a global
             * `.cl-*` override) because that is how the rest of this file speaks to the provider, and it
             * needs no knowledge of when the modal mounts.
             */
            /*
             * The dialog's rounded card. `overflow: hidden` + an explicit radius is load-bearing, not
             * decoration: the card's children — the left navbar (its own --bg-secondary fill) and the
             * scroll box — have SQUARE corners, and without the clip they poke past the card's rounded
             * edge, which reads as "a rounded corner with a second, square corner sitting under it". The
             * clip rounds every inner corner to the card, so the whole dialog is one clean rounded shape.
             */
            /*
             * THE ROUNDED DIALOG. Clerk nests it as modalContent → rootBox → cardBox → (card, navbar,
             * scrollBox), and several of those layers carry a --bg-primary fill with SQUARE corners.
             * Rounding only the cardBox left the OUTER squares (modalContent / rootBox / card) peeking
             * past its rounded corners — "a rounded corner with a second square one under it". The fix:
             *   · `borderRadius` on every layer that paints a background, so no square fill shows at a
             *     corner — this alone resolves the outer squares (a rounded background needs no clip);
             *   · `overflow: hidden` ONLY on the cardBox, whose JOB is to clip its square-cornered
             *     children (the navbar rail, the scroll box). It is kept off the outer layers on purpose:
             *     they host Clerk's own popovers (the "⋯" menus, selects), and clipping there would cut
             *     a dropdown off at the dialog edge.
             * Shared by every Clerk surface, so the organization AND user-account dialogs match.
             */
            rootBox: { borderRadius: '12px' },
            cardBox: {
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                boxShadow: 'none',
                borderRadius: '12px',
                overflow: 'hidden',
            },
            card: { background: 'var(--bg-primary)', boxShadow: 'none', borderRadius: '12px' },
            modalContent: { background: 'var(--bg-primary)', borderRadius: '12px' },
            scrollBox: { background: 'var(--bg-primary)' },
            /*
             * THE LEFT NAV, WEARING OUR TREE.
             *
             * The dialog's page list is the same gesture as the app's own nav tree, so it takes the same
             * treatment: a --bg-secondary rail with a token divider, secondary-coloured items that go
             * primary on hover and on the active page, and a tertiary icon that turns blue where it is
             * selected — the app's tree, in the provider's sidebar.
             */
            navbar: { background: 'var(--bg-secondary)', borderRight: '1px solid var(--border-color)', boxShadow: 'none' },
            navbarButton: {
                color: 'var(--text-secondary)',
                borderRadius: '8px',
                '&:hover': { color: 'var(--text-primary)', background: 'var(--hover-bg)' },
                '&[data-active="true"]': { color: 'var(--text-primary)', background: 'var(--hover-bg)' },
                '&[data-active="true"] .cl-navbarButtonIcon': { color: 'var(--blue)' },
            },
            navbarButtonIcon: { color: 'var(--text-tertiary)' },
        },
    };
}
//# sourceMappingURL=branding.js.map